import DSBox from '../../design-system/DSBox';
import DSAvatar from '../../design-system/DSAvatar';
import { DSTypography } from '../../design-system/Typography';
import KlydoPlayer from './KlydoPlayer';
import { GiftKlydo } from '../../api/gift';
import { Klydo } from '../../api/klydo';
import { PendulumLogoType } from '../../consts/pendulumLogo';
import { KLYDO_BLACK } from '../../consts/colors';
import {
  CLOCK_DIAMETER,
  KLYDO_WIDTH_WITHOUT_FRAME,
} from '../../consts/clockDimensions';

// Default pendulum logo when no category logo is provided (same as editor)
const DEFAULT_PENDULUM_LOGO =
  'https://res.cloudinary.com/klydoclock/image/upload/v1727775595/pack_pendulum_covers/hmzb88yhzidkjujf8xbt.png';

// KlydoPlayer frame padding (from KlydoPlayer.tsx: '40.971px var(--6, 48px)')
const FRAME_PADDING_TOP = 40.971;
const FRAME_PADDING_X = 48;

// Compute the exact pixel position of the clock face circle within the KlydoPlayer frame.
//
// The inner flex DSBox children (clock circle + pendulum) are ALL position:absolute,
// so the inner flex has height=0 (auto with no in-flow content).
// The clock static center Y = inner flex top + 0 (center of 0-height flex).
// Inner flex top from frame = FRAME_PADDING_TOP + marginTop(50% of contentWidth).
// Clock top = innerFlexTop - clockRadius.
const getClockFaceStyle = (scale: number) => {
  const frameWidth = KLYDO_WIDTH_WITHOUT_FRAME * scale;
  const clockDiam = CLOCK_DIAMETER * scale;
  const overlayDiam = CLOCK_DIAMETER * scale + 1;
  const contentWidth = frameWidth - 2 * FRAME_PADDING_X;
  const innerFlexTop = FRAME_PADDING_TOP + contentWidth * 0.5;
  const centerY = innerFlexTop;
  const centerX = frameWidth / 2;
  const top =
    centerY - clockDiam / 2 - 4.5 * scale - (overlayDiam - clockDiam) / 2;
  const left = centerX - overlayDiam / 2 - 5 * scale;
  return { top, left, size: overlayDiam };
};

type GiftKlydoPlayerProps = {
  klydo: GiftKlydo | Partial<Klydo>;
  pendulumLogo?: string;
  // 'full' fills the whole bob untinted and draws no sender name, like the clock.
  pendulumLogoType?: PendulumLogoType;
  senderName?: string;
  senderImageUrl?: string;
  greetingContent?: string;
  date?: number;
  isToggled?: boolean;
  scale?: number;
};

type OverlayProps = Pick<
  GiftKlydoPlayerProps,
  'senderImageUrl' | 'greetingContent' | 'date'
> & { clockSize: number; top: number; left: number };

const GiftMessageOverlay = ({
  senderImageUrl,
  greetingContent,
  date,
  clockSize,
  top,
  left,
}: OverlayProps) => {
  const avatarSize = Math.round(clockSize * 0.28);
  const avatarTop = Math.round(clockSize * 0.05);
  const dateBottom = Math.round(clockSize * 0.06);
  const dateFontSize = Math.round(clockSize * 0.045);
  // Reserve space so centered text doesn't overlap avatar or date
  const paddingTop = avatarTop + avatarSize + 10;
  const paddingBottom = dateBottom + dateFontSize + 4;

  return (
    <DSBox
      sx={{
        position: 'absolute',
        top: `${top}px`,
        left: `${left}px`,
        width: `${clockSize}px`,
        height: `${clockSize}px`,
        borderRadius: '50%',
        border: '2px solid #2D2D2D',
        overflow: 'hidden',
        zIndex: 20,
        backgroundColor: KLYDO_BLACK,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: `${paddingTop}px`,
        paddingBottom: `${paddingBottom}px`,
      }}
    >
      {senderImageUrl && (
        <DSAvatar
          src={senderImageUrl}
          sx={{
            position: 'absolute',
            top: `${avatarTop}px`,
            width: avatarSize,
            height: avatarSize,
          }}
        />
      )}
      {greetingContent && (
        <DSTypography
          sx={{
            fontFamily: 'Gilroy-SemiBold',
            fontSize: `${Math.round(clockSize * 0.038)}px`,
            textAlign: 'center',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxWidth: '75%',
            lineHeight: 1.3,
          }}
        >
          {greetingContent}
        </DSTypography>
      )}
      {date && (
        <DSTypography
          sx={{
            fontFamily: 'Gilroy-SemiBold',
            fontSize: `${dateFontSize}px`,
            opacity: 0.8,
            position: 'absolute',
            bottom: `${dateBottom}px`,
          }}
        >
          {new Date(date).toLocaleString('default', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          })}
        </DSTypography>
      )}
    </DSBox>
  );
};

const GiftKlydoPlayer = ({
  klydo,
  pendulumLogo,
  pendulumLogoType,
  senderName,
  senderImageUrl,
  greetingContent,
  date,
  isToggled = false,
  scale = 0.45,
}: GiftKlydoPlayerProps) => {
  const { top, left, size } = getClockFaceStyle(scale);
  // The default logo shown when none is uploaded is an icon, never "full".
  const isFullLogo = !!pendulumLogo && pendulumLogoType === 'full';

  return (
    <KlydoPlayer
      klydo={klydo}
      pendulumLogo={pendulumLogo || DEFAULT_PENDULUM_LOGO}
      pendulumLogoType={isFullLogo ? 'full' : undefined}
      pendulumLabel={isFullLogo ? undefined : senderName}
      scale={scale}
      muted
    >
      {isToggled && (
        <GiftMessageOverlay
          senderImageUrl={senderImageUrl}
          greetingContent={greetingContent}
          date={date}
          clockSize={size}
          top={top}
          left={left}
        />
      )}
    </KlydoPlayer>
  );
};

export default GiftKlydoPlayer;
